/**
 * Resources configuration — school documents and files hosted on S3.
 *
 * Each resource has:
 *   id         — unique string
 *   name       — display name
 *   description — short description (optional)
 *   category   — groups the resource in the portal UI
 *   fileType   — 'pdf' | 'docx' | 'xlsx' | 'image' | 'link' | 'other'
 *   url        — full S3 URL (public or presigned)
 *   date       — ISO date string of when it was posted
 *   size       — human-readable size string, e.g. "1.2 MB" (optional)
 *   visibleTo  — array of roles that can see this resource:
 *                'student' | 'teacher' | 'parent' | 'all'
 *   pinned     — if true, appears at the top of its category
 */

export const CATEGORIES = [
  'School Calendar',
  'Newsletter',
  'Forms & Applications',
  'Handbooks & Policies',
  'Curriculum',
  'Athletics',
  'Other',
];

export const FILE_TYPES = [
  { value: 'pdf',   label: 'PDF',           ext: '.pdf' },
  { value: 'docx',  label: 'Word Document', ext: '.docx' },
  { value: 'xlsx',  label: 'Spreadsheet',   ext: '.xlsx' },
  { value: 'image', label: 'Image',         ext: '.jpg / .png' },
  { value: 'link',  label: 'External Link', ext: '' },
  { value: 'other', label: 'Other',         ext: '' },
];

const defaultResources = [
  {
    id: 'res-001',
    name: '2024–25 School Calendar',
    description: 'Full academic year calendar with holidays, testing dates, and events.',
    category: 'School Calendar',
    fileType: 'pdf',
    url: 'https://your-bucket.s3.amazonaws.com/calendars/2024-25-school-calendar.pdf',
    date: '2024-08-01',
    size: '0.8 MB',
    visibleTo: ['all'],
    pinned: true,
  },
  {
    id: 'res-002',
    name: 'Spring 2025 Newsletter',
    description: "Principal's message, student highlights, and upcoming events.",
    category: 'Newsletter',
    fileType: 'pdf',
    url: 'https://your-bucket.s3.amazonaws.com/newsletters/spring-2025.pdf',
    date: '2025-01-15',
    size: '2.4 MB',
    visibleTo: ['all'],
    pinned: false,
  },
  {
    id: 'res-003',
    name: 'Fall 2024 Newsletter',
    category: 'Newsletter',
    fileType: 'pdf',
    url: 'https://your-bucket.s3.amazonaws.com/newsletters/fall-2024.pdf',
    date: '2024-09-05',
    size: '2.1 MB',
    visibleTo: ['all'],
    pinned: false,
  },
  {
    id: 'res-004',
    name: 'Student–Parent Handbook 2024–25',
    description: 'Policies, expectations, dress code, and school procedures.',
    category: 'Handbooks & Policies',
    fileType: 'pdf',
    url: 'https://your-bucket.s3.amazonaws.com/handbooks/student-parent-handbook-2024-25.pdf',
    date: '2024-08-01',
    size: '3.1 MB',
    visibleTo: ['student', 'parent'],
    pinned: true,
  },
  {
    id: 'res-005',
    name: 'Staff Handbook 2024–25',
    category: 'Handbooks & Policies',
    fileType: 'pdf',
    url: 'https://your-bucket.s3.amazonaws.com/handbooks/staff-handbook-2024-25.pdf',
    date: '2024-08-01',
    size: '2.7 MB',
    visibleTo: ['teacher'],
    pinned: false,
  },
  {
    id: 'res-006',
    name: 'Field Trip Permission Slip',
    description: 'General permission slip template — print and return signed.',
    category: 'Forms & Applications',
    fileType: 'pdf',
    url: 'https://your-bucket.s3.amazonaws.com/forms/field-trip-permission.pdf',
    date: '2024-08-15',
    size: '0.2 MB',
    visibleTo: ['parent'],
    pinned: false,
  },
  {
    id: 'res-007',
    name: 'Medical Release Form',
    category: 'Forms & Applications',
    fileType: 'pdf',
    url: 'https://your-bucket.s3.amazonaws.com/forms/medical-release.pdf',
    date: '2024-08-15',
    size: '0.3 MB',
    visibleTo: ['parent'],
    pinned: false,
  },
  {
    id: 'res-008',
    name: 'Curriculum Overview — 2024–25',
    description: 'Subject syllabi and learning objectives by grade level.',
    category: 'Curriculum',
    fileType: 'pdf',
    url: 'https://your-bucket.s3.amazonaws.com/curriculum/overview-2024-25.pdf',
    date: '2024-08-01',
    size: '1.5 MB',
    visibleTo: ['teacher', 'parent'],
    pinned: false,
  },
  {
    id: 'res-009',
    name: 'Athletics Schedule — Spring 2025',
    category: 'Athletics',
    fileType: 'pdf',
    url: 'https://your-bucket.s3.amazonaws.com/athletics/spring-2025-schedule.pdf',
    date: '2025-01-10',
    size: '0.4 MB',
    visibleTo: ['all'],
    pinned: false,
  },
];

const STORAGE_KEY = 'eraResources';

export const getResources = () => {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) return JSON.parse(saved);
  } catch (e) { /* ignore */ }
  return defaultResources;
};

export const saveResources = (resources) => {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(resources));
};

export const resetResources = () => {
  localStorage.removeItem(STORAGE_KEY);
  return defaultResources;
};

/** Filter resources visible to a given portal role */
export const getResourcesForRole = (role) =>
  getResources().filter(r =>
    r.visibleTo.includes('all') || r.visibleTo.includes(role)
  );
